import React, { useState } from 'react';
import type { Pathway, PathwayBlock, LevelReviewEntry } from '../types';
import {
    CheckCircle2, Lock, Circle, ArrowDown, ArrowLeftRight, RefreshCw, Clock, XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { renderBlockIcon, getBlockTypeDisplayName } from '../config/blockTypes';

// --- Types ---

type BlockStatus = 'locked' | 'available' | 'in_progress' | 'complete';

export interface PathwayProgressFlowProps {
    pathway: Pathway;
    blockProgressMap: Map<string, string>;
    selectedTrack?: string;
    onSelectBlock?: (block: PathwayBlock) => void;
    levelReviews?: Record<string, LevelReviewEntry>;
    onSubmitLevel?: (groupKey: string) => Promise<void> | void;
}

// --- Config ---

const TRACK_CONFIG: Record<string, { label: string; badge: string }> = {
    STANDARD:    { label: 'Standard (Novice)',   badge: 'bg-blue-100 text-blue-700 border border-blue-200' },
    ACCELERATED: { label: 'Accelerated (RPL)',   badge: 'bg-purple-100 text-purple-700 border border-purple-200' },
    REFRESHER:   { label: 'Refresher (Renewal)', badge: 'bg-orange-100 text-orange-700 border border-orange-200' },
    REMEDIAL:    { label: 'Remedial',            badge: 'bg-red-100 text-red-700 border border-red-200' },
};

const STATUS_STYLE: Record<BlockStatus, { border: string; bg: string; opacity: string }> = {
    complete:   { border: 'border-green-400',  bg: 'bg-green-50',  opacity: '' },
    in_progress:{ border: 'border-blue-400',   bg: 'bg-blue-50',   opacity: '' },
    available:  { border: 'border-aero-300',   bg: 'bg-white',     opacity: '' },
    locked:     { border: 'border-aero-200',   bg: 'bg-aero-50',   opacity: 'opacity-50' },
};

// --- Helpers ---

function groupBlocks(blocks: PathwayBlock[]): PathwayBlock[][] {
    const groups: PathwayBlock[][] = [];
    const seen = new Set<string>();
    for (const block of blocks) {
        if (seen.has(block.id)) continue;
        if (block.horizontalGroup) {
            const group = blocks
                .filter(b => b.horizontalGroup === block.horizontalGroup)
                .sort((a, b) => (a.horizontalPosition || 0) - (b.horizontalPosition || 0));
            group.forEach(b => seen.add(b.id));
            groups.push(group);
        } else {
            seen.add(block.id);
            groups.push([block]);
        }
    }
    return groups;
}

// --- Block Card ---

const BlockCard: React.FC<{
    block: PathwayBlock;
    status: BlockStatus;
    index: number;
    onSelect?: (block: PathwayBlock) => void;
    readOnly?: boolean;
}> = ({ block, status, index, onSelect, readOnly }) => {
    const style = STATUS_STYLE[status];
    const isClickable = !readOnly && onSelect && (status === 'available' || status === 'in_progress' || block.type === 'pathway');

    return (
        <div
            onClick={() => isClickable && onSelect!(block)}
            className={`relative rounded-lg border-2 shadow-sm w-52 p-3 transition-all
                ${style.border} ${style.bg} ${style.opacity}
                ${isClickable ? 'cursor-pointer hover:shadow-md hover:border-aero-500' : ''}
            `}
        >
            {/* Index badge */}
            <div className="absolute -top-2 -left-2 w-5 h-5 bg-aero-800 text-white rounded-full flex items-center justify-center text-[10px] font-bold z-10 border-2 border-white shadow-sm">
                {index + 1}
            </div>

            {/* Status icon top-right */}
            <div className="absolute top-2 right-2">
                {status === 'complete'    && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                {status === 'in_progress' && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />}
                {status === 'locked'      && <Lock className="w-4 h-4 text-aero-300" />}
                {status === 'available'   && <Circle className="w-4 h-4 text-aero-400" />}
            </div>

            {/* Block info */}
            <div className="flex items-center gap-2 mb-1">
                {renderBlockIcon(block.type)}
                <span className="text-[10px] uppercase font-bold text-aero-400 tracking-wide">
                    {getBlockTypeDisplayName(block.type)}
                </span>
            </div>
            <div className="font-bold text-sm text-aero-900 leading-tight pr-5">{block.name}</div>
            {block.description && (
                <div className="text-xs text-aero-500 mt-0.5 line-clamp-2">{block.description}</div>
            )}
            {block.type === 'theory' && block.courseTemplateName && (
                <div className="mt-1.5 px-2 py-1 bg-blue-50 rounded border border-blue-100 text-[10px] text-blue-600 font-medium truncate">
                    {block.courseTemplateName}
                </div>
            )}
            {status === 'complete' && (
                <div className="flex items-center gap-1 mt-1.5">
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    <span className="text-[10px] font-bold text-green-600 uppercase tracking-wide">Signed Off</span>
                </div>
            )}
        </div>
    );
};

// --- Section renderer ---

const FlowSection: React.FC<{
    blocks: PathwayBlock[];
    statusMap: Map<string, BlockStatus>;
    startIndex: number;
    onSelectBlock?: (block: PathwayBlock) => void;
    showArrowAbove?: boolean;
}> = ({ blocks, statusMap, startIndex, onSelectBlock, showArrowAbove }) => {
    if (blocks.length === 0) return null;
    const groups = groupBlocks(blocks);
    let blockCounter = startIndex;

    return (
        <>
            {showArrowAbove && (
                <div className="flex justify-center my-1">
                    <ArrowDown className="w-4 h-4 text-aero-300" />
                </div>
            )}
            {groups.map((group, gIdx) => {
                const content = (
                    <div key={gIdx} className="flex flex-col items-center">
                        {group.length > 1 ? (
                            <>
                                <div className="flex items-center gap-4 justify-center">
                                    {group.map((block, bIdx) => {
                                        const idx = blockCounter++;
                                        return (
                                            <div key={block.id} className="flex items-center gap-4">
                                                {bIdx > 0 && (
                                                    <div className="flex items-center gap-1">
                                                        <ArrowLeftRight className="w-3 h-3 text-aero-300" />
                                                        <span className="text-[10px] font-bold text-aero-400">AND</span>
                                                    </div>
                                                )}
                                                <BlockCard
                                                    block={block}
                                                    status={statusMap.get(block.id) || 'locked'}
                                                    index={idx}
                                                    onSelect={onSelectBlock}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="text-[9px] text-aero-400 italic mt-1 bg-aero-50 px-2 py-0.5 rounded">
                                    Complete all in this level
                                </div>
                            </>
                        ) : (
                            <BlockCard
                                block={group[0]}
                                status={statusMap.get(group[0].id) || 'locked'}
                                index={blockCounter++}
                                onSelect={onSelectBlock}
                            />
                        )}
                        {gIdx < groups.length - 1 && (
                            <div className="flex justify-center my-1">
                                <ArrowDown className="w-4 h-4 text-aero-300" />
                            </div>
                        )}
                    </div>
                );
                return content;
            })}
        </>
    );
};

// --- Main Component ---

export const PathwayProgressFlow: React.FC<PathwayProgressFlowProps> = ({
    pathway,
    blockProgressMap,
    selectedTrack,
    onSelectBlock,
    levelReviews,
    onSubmitLevel,
}) => {
    const { t } = useTranslation();
    const [submittingKey, setSubmittingKey] = useState<string | null>(null);
    const allBlocks = pathway.blocks || [];

    // Separate blocks into sections
    const trackBlocks = selectedTrack
        ? allBlocks.filter(b => !b.branchId || b.branchId === selectedTrack)
        : allBlocks.filter(b => b.branchId !== 'MERGE' && b.branchId !== 'REMEDIAL');
    const mergeBlocks  = allBlocks.filter(b => b.branchId === 'MERGE');
    const remedialBlocks = allBlocks.filter(b => b.branchId === 'REMEDIAL');

    // Compute statuses sequentially across all visible blocks
    const orderedAll = [...trackBlocks, ...mergeBlocks, ...remedialBlocks];
    const allGroups = groupBlocks(orderedAll);
    const statusMap = new Map<string, BlockStatus>();
    let prevGroupComplete = true;

    for (const group of allGroups) {
        const allComplete = group.every(b => blockProgressMap.get(b.id) === 'complete');
        group.forEach(b => {
            const progress = blockProgressMap.get(b.id);
            let status: BlockStatus;
            if (progress === 'complete') {
                status = 'complete';
            } else if (!prevGroupComplete) {
                status = 'locked';
            } else if (progress === 'in_progress') {
                status = 'in_progress';
            } else {
                status = 'available';
            }
            statusMap.set(b.id, status);
        });
        if (!allComplete) prevGroupComplete = false;
    }

    const completeCount = orderedAll.filter(b => statusMap.get(b.id) === 'complete').length;
    const totalCount = orderedAll.length;
    const progressPct = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0;
    const trackCfg = selectedTrack ? TRACK_CONFIG[selectedTrack] : null;

    const handleSubmitLevel = async (groupKey: string) => {
        if (!onSubmitLevel) return;
        setSubmittingKey(groupKey);
        try {
            await onSubmitLevel(groupKey);
        } finally {
            setSubmittingKey(null);
        }
    };

    let blockCounter = 0;

    return (
        <div className="flex flex-col items-center py-4 px-2 min-w-0">
            {/* Track badge + progress */}
            <div className="flex flex-col items-center gap-2 mb-4 w-full max-w-xs">
                {trackCfg && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${trackCfg.badge}`}>
                        {trackCfg.label}
                    </span>
                )}
                <div className="w-full bg-aero-100 rounded-full h-1.5">
                    <div
                        className={`h-1.5 rounded-full transition-all ${progressPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
                <p className="text-xs text-aero-500">{completeCount} of {totalCount} blocks complete · {progressPct}%</p>
            </div>

            {/* Track blocks */}
            {trackBlocks.length > 0 && (() => {
                const groups = groupBlocks(trackBlocks);
                return groups.map((group, gIdx) => {
                    const groupKey = `level_${gIdx}`;
                    const review = levelReviews?.[groupKey];
                    const allBlocksComplete = group.every(b => statusMap.get(b.id) === 'complete');
                    const anyInProgressOrComplete = group.some(b => {
                        const s = statusMap.get(b.id);
                        return s === 'complete' || s === 'in_progress';
                    });
                    const isSubmitted = review?.status === 'submitted';
                    const isApproved = review?.status === 'approved';
                    const isRejected = review?.status === 'rejected';
                    const readOnly = isSubmitted;
                    const canSubmit =
                        !!onSubmitLevel &&
                        (!review || review.status === 'rejected') &&
                        allBlocksComplete &&
                        anyInProgressOrComplete;
                    const isSubmitting = submittingKey === groupKey;

                    const content = (
                        <div key={`track-${gIdx}`} className="flex flex-col items-center w-full">
                            {group.length > 1 ? (
                                <>
                                    <div className="flex items-center gap-4 justify-center">
                                        {group.map((block, bIdx) => {
                                            const idx = blockCounter++;
                                            return (
                                                <div key={block.id} className="flex items-center gap-4">
                                                    {bIdx > 0 && (
                                                        <div className="flex items-center gap-1">
                                                            <ArrowLeftRight className="w-3 h-3 text-aero-300" />
                                                            <span className="text-[10px] font-bold text-aero-400">AND</span>
                                                        </div>
                                                    )}
                                                    <BlockCard
                                                        block={block}
                                                        status={statusMap.get(block.id) || 'locked'}
                                                        index={idx}
                                                        onSelect={onSelectBlock}
                                                        readOnly={readOnly}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="text-[9px] text-aero-400 italic mt-1 bg-aero-50 px-2 py-0.5 rounded">
                                        Complete all in this level
                                    </div>
                                </>
                            ) : (
                                <BlockCard
                                    block={group[0]}
                                    status={statusMap.get(group[0].id) || 'locked'}
                                    index={blockCounter++}
                                    onSelect={onSelectBlock}
                                    readOnly={readOnly}
                                />
                            )}
                            {/* Level review status / submit button */}
                            {isSubmitted && (
                                <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-[10px] font-bold text-yellow-700">
                                    <Clock className="w-3 h-3" /> {t('candidate.pathway.levelAwaitingReview')}
                                </div>
                            )}
                            {isRejected && (
                                <div className="mt-2 flex items-start gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700 max-w-xs">
                                    <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="font-bold">{t('candidate.pathway.levelRejected')}</div>
                                        {review?.rejectionReason && (
                                            <div className="italic text-red-600 break-words">
                                                {t('candidate.pathway.levelRejectedReason', { reason: review.rejectionReason })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isApproved && (
                                <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-[10px] font-bold text-green-700">
                                    <CheckCircle2 className="w-3 h-3" /> {t('candidate.pathway.levelApproved')}
                                </div>
                            )}
                            {canSubmit && (
                                <button
                                    type="button"
                                    onClick={() => handleSubmitLevel(groupKey)}
                                    disabled={isSubmitting}
                                    className="mt-2 px-3 py-1.5 bg-aero-800 hover:bg-aero-700 disabled:opacity-50 text-white text-[11px] font-semibold rounded-lg shadow-sm transition-colors"
                                >
                                    {isSubmitting
                                        ? t('candidate.pathway.submittingLevel')
                                        : t('candidate.pathway.submitLevel')}
                                </button>
                            )}
                            <div className="flex justify-center my-1">
                                <ArrowDown className="w-4 h-4 text-aero-300" />
                            </div>
                        </div>
                    );
                    return content;
                });
            })()}

            {/* Merge / Final Assessment section */}
            {mergeBlocks.length > 0 && (
                <>
                    <div className="flex items-center gap-2 my-2 w-full max-w-xs">
                        <div className="flex-1 h-px bg-aero-200" />
                        <span className="text-[10px] font-bold text-aero-500 uppercase tracking-widest px-2">Final Assessment</span>
                        <div className="flex-1 h-px bg-aero-200" />
                    </div>
                    <FlowSection
                        blocks={mergeBlocks}
                        statusMap={statusMap}
                        startIndex={blockCounter}
                        onSelectBlock={onSelectBlock}
                    />
                    {(() => { blockCounter += mergeBlocks.length; return null; })()}
                </>
            )}

            {/* Remedial section */}
            {remedialBlocks.length > 0 && (
                <>
                    <div className="flex items-center gap-2 my-2 w-full max-w-xs">
                        <div className="flex-1 h-px bg-red-200" />
                        <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest px-2">Remedial Loop</span>
                        <div className="flex-1 h-px bg-red-200" />
                    </div>
                    <FlowSection
                        blocks={remedialBlocks}
                        statusMap={statusMap}
                        startIndex={blockCounter}
                        onSelectBlock={onSelectBlock}
                        showArrowAbove
                    />
                </>
            )}

            {/* SoloReady end state */}
            {mergeBlocks.length > 0 && (
                <div className="flex flex-col items-center mt-2">
                    <ArrowDown className="w-4 h-4 text-aero-300 mb-1" />
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-bold shadow-sm ${
                        progressPct === 100
                            ? 'bg-green-50 border-green-400 text-green-700'
                            : 'bg-aero-50 border-aero-200 text-aero-400'
                    }`}>
                        <CheckCircle2 className="w-4 h-4" />
                        SoloReady Certified
                    </div>
                </div>
            )}
        </div>
    );
};
